/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Alexander Golshtein
 * @version 1.24.0
 * @since 1.21.0
 */

import * as Yup from 'yup';

import { getFormLocale } from '@modules/form/const/formLocalization';
import { ICorporateFormTranslation } from '@modules/internationalization/types/corporate';

import {
    ECreateInquiryField as F,
    ECreateInquiryFieldType as Type,
    ECreateInquiryFieldVisibility as Visibility,
} from '../constants';
import type {
	TFailedFileAttach,
	ICreateInquiryAttribute,
	ICreateInquiryServiceAttribute,
	TSuccessFileAttach,
	TCreateInquiryFormValidation,
} from '../types';

export const setInquiryValidationScheme = (
    translation: ICorporateFormTranslation['validation'],
    customAttrs: ICreateInquiryAttribute[],
    serviceAttributes?: ICreateInquiryServiceAttribute,
): TCreateInquiryFormValidation => {
	const phoneRegExp = new RegExp('^(9)[0-9]{9}$');
    const attributeValidationScheme: TCreateInquiryFormValidation = {};

    Yup.setLocale(getFormLocale(translation));

	customAttrs
		.filter(
			(attribute: ICreateInquiryAttribute) =>
				attribute.declaration.accessibility !== Visibility.hidden &&
				attribute.declaration.accessibility !== Visibility.readOnly,
		)
		.forEach((attribute: ICreateInquiryAttribute) => {
			if (attribute.declaration.type === Type.dbQuery) return;

			// set attribute regex
			let attributeRegex = new RegExp(/[^]*$/);
			if (attribute.declaration.regexpRule && attribute.declaration.regexpRule.regExpText) {
				attributeRegex = new RegExp(attribute.declaration.regexpRule.regExpText, 'i');
			}

			const isRequired = attribute.declaration.accessibility === Visibility.mandatory;

			attributeValidationScheme[attribute.code.toString()] = {
				[Type.string]: () => {
					const schema = Yup.string();
					if (attributeRegex) {
						schema.matches(attributeRegex, attribute.declaration?.regexpRule?.warningMessage);
					}
					return isRequired ? schema.required() : schema.nullable(true);
				},
				[Type.number]: () => {
					const schema = Yup.number();
					return isRequired ? schema.required() : schema.nullable(true);
				},
				[Type.date]: () => {
					const schema = Yup.date();
					return isRequired ? schema.required() : schema.nullable(true);
				},
				[Type.bool]: () => {
					const schema = Yup.boolean();
					return isRequired ? schema.required() : schema.nullable(true);
				},
				[Type.dictionary]: () => {
					if (attribute.declaration.multiSelect) {
						const schema = Yup.array().min(1).of(
							Yup.object().shape({
								value: isRequired ? Yup.string().required() : Yup.string(),
								label: isRequired ? Yup.string().required() : Yup.string(),
							}),
						);
						return isRequired ? schema.required() : schema.nullable(true);
					} else {
						const schema = Yup.object().shape({
							value: isRequired ? Yup.string().required() : Yup.string(),
							label: isRequired ? Yup.string().required() : Yup.string(),
						});
						return isRequired ? schema.required() : schema.nullable(true);
					}
				},
			}[attribute.declaration.type]();
		});

	attributeValidationScheme[F.phone] = Yup.string().length(10).matches(phoneRegExp);
	attributeValidationScheme[F.email] = Yup.string().email();
	attributeValidationScheme[F.description] = Yup.string();

	if (serviceAttributes?.additionalAvailable) {
		attributeValidationScheme[F.email] = attributeValidationScheme[F.email].required();
	} else {
		attributeValidationScheme[F.description] = attributeValidationScheme[F.description].required();
	}

	return attributeValidationScheme;
};

export const isSuccessFileAttach = (
	item: TSuccessFileAttach | TFailedFileAttach,
): item is TSuccessFileAttach => {
	return Object.prototype.hasOwnProperty.call(item, 'data');
};

export const isFailedFileAttach = (
	item: TSuccessFileAttach | TFailedFileAttach,
): item is TFailedFileAttach => {
	return Object.prototype.hasOwnProperty.call(item, 'error');
};

